import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBalance } from '@app/shared/hooks/useBalance';
import { telegramVibrate } from '@app/shared/utils/common';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { usePurchaseSettlement } from '@app/shared/hooks/usePurchaseSettlement';
import { useBoostSettlementMutation } from '@app/store/slices/settlements/settlementsApi';
import { Settlement as SettlementType } from '@app/store/slices/settlements/settlementsTypes';
import { SettlementModalState, SettlementModalAction } from '@app/shared/types/pages/home';
import { PriceType } from '@app/shared/types/common';
import notification from '@app/shared/utils/notification';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useLocation } from 'react-router-dom';

const INITIAL_SETTLEMENT_MODAL_STATE: SettlementModalState = {
  isOpen: false,
};

export const useSettlementModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((state) => state.tutorial);
  const { pathname } = useLocation();
  const { handleFetchError } = useApiErrorHandler();
  const { isBalanceEnough } = useBalance();
  const { purchaseSettlement, isPurchasingSettlement } = usePurchaseSettlement();

  const [settlementModalState, setSettlementModalState] = useState<SettlementModalState>(
    INITIAL_SETTLEMENT_MODAL_STATE
  );

  const [boostSettlement, { isLoading: isBoostingSettlement }] = useBoostSettlementMutation();

  const handleCompleteSettlementTutorial = () => {
    if (
      !tutorial.complete &&
      tutorial.step === 'HOME_SETTLEMENT_1' &&
      settlementModalState.settlement?.nodeLevel === 0 &&
      pathname === '/'
    ) {
      dispatch(tutorialActions.onSettlementClick());
    }
  };

  const handleSettlementModalOpen = (settlement: SettlementType, action: SettlementModalAction) => {
    setSettlementModalState({ isOpen: true, settlement, action, isBoosting: false });
  };

  const handleSettlementModalClose = () => {
    telegramVibrate();
    setSettlementModalState(INITIAL_SETTLEMENT_MODAL_STATE);
    handleCompleteSettlementTutorial();
  };

  const toggleIsBoosting = () => {
    setSettlementModalState((prev) => ({ ...prev, isBoosting: !prev.isBoosting }));
  };

  const handlePurchaseSettlement = async (settlement: SettlementType) => {
    await purchaseSettlement(settlement, handleSettlementModalClose);
  };

  const handleSettlementBoost = async (settlement: SettlementType, paymentType: PriceType) => {
    telegramVibrate();

    const price =
      paymentType === PriceType.COOKIE ? settlement.boostCookiePrice : settlement.boostTonPrice;

    if (!isBalanceEnough(paymentType, price)) {
      notification.error(t('errors.balance'));
      return;
    }

    try {
      const response = await boostSettlement({
        settlementId: settlement.id,
        amount: price,
        paymentType,
      }).unwrap();

      if (response.ok) {
        handleSettlementModalClose();
        notification.success(
          t('settlements.upgrade_success', { settlementTitle: settlement.name })
        );
      }
    } catch (err) {
      handleFetchError(err);
    }
  };

  return {
    settlementModalState,
    handleSettlementModalOpen,
    handleSettlementModalClose,
    toggleIsBoosting,
    handlePurchaseSettlement,
    handleSettlementBoost,
    isPurchasingSettlement,
    isBoostingSettlement,
  };
};
